import { Suspense } from 'react';

import { bindToggle } from 'material-ui-popup-state/core';
import { PopupState } from 'material-ui-popup-state/hooks';

import { tokens, Typography, Box, Skeleton } from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { Organization, OrganizationType } from '@cast/types';

import { useIsViewingEnterpriseOrg } from '../../../_hooks/useIsViewingEnterpriseOrg';
import { NavTriggerCaret } from '../../NavTriggerCaret';
import {
  getNavTriggerButtonSx,
  navTriggerGap,
  navTriggerLabelSx,
  navTriggerPaddingX,
} from '../../navTriggerStyles';

type OrganizationMenuTriggerProps = {
  activeOrganization: Organization;
  activeOrganizationParent?: Organization;
  popupState: PopupState;
};

type OrganizationMenuTriggerContentProps = OrganizationMenuTriggerProps & {
  isViewingEnterprise: boolean;
};

const OrganizationMenuTriggerContent = ({
  activeOrganization,
  activeOrganizationParent,
  popupState,
  isViewingEnterprise,
}: OrganizationMenuTriggerContentProps) => {
  const showSingleIcon =
    isViewingEnterprise ||
    activeOrganization.type === OrganizationType.DEFAULT ||
    activeOrganization.type === OrganizationType.ENTERPRISE;

  const displayOrg =
    isViewingEnterprise && activeOrganizationParent
      ? activeOrganizationParent
      : activeOrganization;

  return (
    <Box
      component="button"
      type="button"
      {...bindToggle(popupState)}
      display="inline-flex"
      alignItems="center"
      gap={navTriggerGap}
      pl="2px"
      pr={navTriggerPaddingX}
      data-testid="org-menu-list-btn"
      data-active-org-id={activeOrganization.id}
      sx={getNavTriggerButtonSx(popupState.isOpen)}
    >
      <Box position="relative" display="inline-flex" flexShrink={0}>
        {showSingleIcon ? (
          <OrganizationAvatar size="sm" organizationId={displayOrg.id} />
        ) : (
          <>
            <OrganizationAvatar
              size="sm"
              organizationId={activeOrganizationParent?.id}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-25%',
                right: '-25%',
              }}
            >
              <OrganizationAvatar
                size="2xs"
                organizationId={activeOrganization.id}
              />
            </Box>
          </>
        )}
      </Box>

      <Typography
        variant="P14B"
        noWrap
        sx={{ ...navTriggerLabelSx, color: tokens.theme.fg.action.onGhost }}
      >
        {displayOrg.name}
      </Typography>

      <NavTriggerCaret />
    </Box>
  );
};

const OrganizationMenuTriggerInner = (props: OrganizationMenuTriggerProps) => {
  const isViewingEnterprise = useIsViewingEnterpriseOrg();

  return (
    <OrganizationMenuTriggerContent
      {...props}
      isViewingEnterprise={isViewingEnterprise}
    />
  );
};

export const OrganizationMenuTrigger = (
  props: OrganizationMenuTriggerProps
) => {
  return (
    // Necessary because the component retrieves an async atom value from useIsViewingEnterpriseOrg
    <Suspense fallback={<Skeleton width={150} height={30} />}>
      <OrganizationMenuTriggerInner {...props} />
    </Suspense>
  );
};
